import { Head, router } from '@inertiajs/react'
import { ClockIcon } from '@heroicons/react/24/outline'
import { withAppLayout, PageHeading } from '@/components/layout'
import {
  Field,
  Label,
  Select,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '@/components/ui'
import { Pagination } from '@/components/common/Pagination'
import { StatusPill } from '@/components/common/StatusPill'
import { MonoId } from '@/components/common/MonoId'
import { EmptyState } from '@/components/common/EmptyState'
import { useTranslations } from '@/lib/i18n'
import { formatMoney, formatDateTime } from '@/lib/format'
import type { PageResult, ActivityRow, StatusFamily } from '@/types'

interface CentralActivityRow extends ActivityRow {
  actor_role: string | null
  gateway_transaction_id: string | null
  merchant: string | null
}

const ACTION_STATUS_FAMILY: Record<string, StatusFamily> = { succeeded: 'success', failed: 'failed' }

function ActivityIndex({
  activity,
  actors,
  filter,
}: {
  activity: PageResult<CentralActivityRow>
  actors: { id: number; name: string }[]
  filter: { user_id: number | null }
}) {
  const { t, locale } = useTranslations()
  const { items, pagination } = activity

  const navigate = (params: Record<string, string | number | undefined>) =>
    router.get('/activity', params, { preserveScroll: true, preserveState: true, replace: true })

  return (
    <>
      <Head title={t('activity.title')} />
      <PageHeading title={t('activity.title')} subtitle={t('activity.oversight_subtitle')} />

      <div className="mt-6 flex flex-col gap-6">
        {actors.length > 0 && (
          <Field className="max-w-xs">
            <Label>{t('activity.filters.actor')}</Label>
            <Select
              value={filter.user_id ?? ''}
              onChange={(e) => navigate({ user_id: e.target.value || undefined, page: 1 })}
            >
              <option value="">{t('activity.filters.all_actors')}</option>
              {actors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {items.length === 0 ? (
          <EmptyState icon={ClockIcon} title={t('activity.empty')} />
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeader>{t('activity.columns.date')}</TableHeader>
                  <TableHeader>{t('activity.columns.actor')}</TableHeader>
                  <TableHeader>{t('activity.columns.merchant')}</TableHeader>
                  <TableHeader>{t('activity.columns.action')}</TableHeader>
                  <TableHeader className="text-right">{t('activity.columns.amount')}</TableHeader>
                  <TableHeader>{t('activity.columns.status')}</TableHeader>
                  <TableHeader>{t('activity.columns.transaction')}</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap">{formatDateTime(row.created_at, locale)}</TableCell>
                    <TableCell>
                      <div className="text-text-strong">{row.actor ?? '—'}</div>
                      {row.actor_role && <div className="text-text/60">{t(`roles.${row.actor_role}`)}</div>}
                    </TableCell>
                    <TableCell>{row.merchant ?? '—'}</TableCell>
                    <TableCell>{t(`activity.actions.${row.action}`)}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {row.amount && row.currency ? formatMoney(row.amount, row.currency, locale) : '—'}
                    </TableCell>
                    <TableCell>
                      {row.status ? (
                        <StatusPill family={ACTION_STATUS_FAMILY[row.status] ?? 'neutral'}>
                          {t(`activity.statuses.${row.status}`)}
                        </StatusPill>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {row.gateway_transaction_id ? (
                        <MonoId value={row.gateway_transaction_id} href={`/payments/${row.gateway_transaction_id}`} />
                      ) : (
                        <span className="text-text/50">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Pagination
              pagination={pagination}
              onNavigate={(n) => navigate({ user_id: filter.user_id ?? undefined, page: n })}
            />
          </>
        )}
      </div>
    </>
  )
}

ActivityIndex.layout = withAppLayout

export default ActivityIndex
